/**
 * Compras — Notas fiscais. Rota `/app/compras/notas-fiscais`.
 *
 * O que entrou na loja, de quem, por quanto — e o que ainda falta fazer com
 * cada nota. Cada linha se lê em meio segundo: fornecedor à esquerda, valor e
 * situação de estoque à direita.
 *
 * A tela é um RECORTE, não um despejo. Três decisões sustentam isso:
 *
 *  · Os três KPIs recortam a tabela em vez de só informar. "Itens sem SKU",
 *    "esperando lançar" e "sem EAN no XML" são as três coisas que travam a
 *    entrada de estoque — e são clicáveis, porque um número que não muda
 *    decisão nenhuma não merece a dobra da tela.
 *
 *  · Cor só onde alguém precisa agir. "Lançada" é o desfecho normal e fica em
 *    cinza; âmbar é o que espera decisão e vermelho é o que travou (spec 01).
 *    Verde em toda linha não classificaria nada.
 *
 *  · Filtro, contagem, soma e paginação são do SERVIDOR. Filtrar em memória
 *    daria certo hoje e mentiria no dia em que a loja tiver mais notas do que
 *    a primeira página — o pé da tabela diria "soma do recorte" somando só o
 *    que coube na tela.
 *
 * Clicar numa linha abre a MESMA ficha (`FichaNotaFiscal`) que a importação
 * mostra depois de gravar — é o componente compartilhado que garante que
 * conciliar e lançar se comportam igual nas duas telas.
 */
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { LinkBotao } from '../../componentes/Botao'
import { CabecalhoDaGaveta, CorpoDaGaveta, Gaveta } from '../../componentes/Gaveta'
import { Icone } from '../../componentes/Icone'
import {
  CartaoKPI,
  Chips,
  MenuDeLinha,
  Paginacao,
  VazioFiltrado,
  VazioPrimeiraVez,
  type AcaoDeLinha,
} from '../../componentes/Listagem'
import { Selo } from '../../componentes/Selo'
import { ErroApi } from '../../plataforma/api'
import { formatar, formatarMoeda } from '../../plataforma/dinheiro'
import { carregarFornecedores, type LinhaFornecedor } from '../../plataforma/fornecedores'
import {
  carregarNotasFiscais,
  intervaloDoMes,
  lancarNoEstoque,
  mesesRecentes,
  type LinhaNota,
  type ListaDeNotas,
  type Recorte,
  type SituacaoEstoque,
} from '../../plataforma/notasfiscais'
import { LayoutApp } from '../app/LayoutApp'
import { FichaNotaFiscal } from './FichaNotaFiscal'

/** `''` é "todo o período" — e é o padrão, porque nota de compra chega quando
 * o fornecedor manda, não quando o mês vira. */
type Mes = string

/** O que a revisão passa pela rota depois de gravar. */
type EstadoDaVolta = {
  chaveImportada?: string
  avisoImportacao?: string
}

export function TelaListaDeNotasFiscais() {
  const meses = useMemo(() => mesesRecentes(), [])
  const local = useLocation()
  const navegar = useNavigate()

  const [mes, setMes] = useState<Mes>('')
  const [situacao, setSituacao] = useState<SituacaoEstoque | ''>('')
  const [recorte, setRecorte] = useState<Recorte | ''>('')
  const [fornecedor, setFornecedor] = useState('')
  const [busca, setBusca] = useState('')
  const [buscaAtiva, setBuscaAtiva] = useState('')
  const [pagina, setPagina] = useState(1)

  const [lista, setLista] = useState<ListaDeNotas | null>(null)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [versao, setVersao] = useState(0)
  const [chaveAberta, setChaveAberta] = useState<string | null>(null)
  const [lancando, setLancando] = useState<string | null>(null)

  /* Quem acabou de importar chega aqui pela revisão: abre a nota gravada e
     mostra o que aconteceu, em vez de largar a pessoa numa lista onde ela
     precisa procurar a própria nota. O state é consumido uma vez — sem isso,
     um F5 repetiria o aviso de um lançamento que já passou. */
  useEffect(() => {
    const volta = local.state as EstadoDaVolta | null
    if (!volta?.chaveImportada && !volta?.avisoImportacao) return

    if (volta.chaveImportada) setChaveAberta(volta.chaveImportada)
    if (volta.avisoImportacao) setAviso(volta.avisoImportacao)
    navegar(local.pathname, { replace: true, state: null })
  }, [])

  // Debounce da busca: cada tecla disparando uma consulta que agrega itens de
  // todas as notas é como se derruba o próprio banco em dia de movimento.
  useEffect(() => {
    const relogio = setTimeout(() => {
      setBuscaAtiva(busca)
      setPagina(1)
    }, 300)
    return () => clearTimeout(relogio)
  }, [busca])

  const periodo = useMemo(() => (mes ? intervaloDoMes(mes) : null), [mes])

  useEffect(() => {
    const controle = new AbortController()
    carregarNotasFiscais(
      {
        de: periodo?.de,
        ate: periodo?.ate,
        fornecedor,
        situacao,
        recorte,
        busca: buscaAtiva,
        pagina,
      },
      controle.signal,
    )
      .then((dados) => {
        setLista(dados)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar as notas fiscais.')
      })
    return () => controle.abort()
  }, [periodo, fornecedor, situacao, recorte, buscaAtiva, pagina, versao])

  /* Lista curta e estável: vale carregar uma vez e não a cada filtro. */
  const [fornecedores, setFornecedores] = useState<LinhaFornecedor[]>([])
  useEffect(() => {
    const controle = new AbortController()
    carregarFornecedores('', 'todos', controle.signal)
      // `?? []` e não `dados.linhas` cru: o filtro é conveniência, e uma
      // resposta sem linhas — base vazia, corpo abreviado — não pode derrubar
      // a tela inteira das notas.
      .then((dados) => setFornecedores(dados.linhas ?? []))
      .catch(() => {
        // O filtro de fornecedor é conveniência: sem ele a tela continua
        // inteira, e um erro aqui não pode roubar a lista de notas.
      })
    return () => controle.abort()
  }, [])

  const contagens = lista?.contagens
  const recortado = !!buscaAtiva || !!situacao || !!recorte || !!fornecedor

  function limparRecorte() {
    setBusca('')
    setBuscaAtiva('')
    setSituacao('')
    setRecorte('')
    setFornecedor('')
    setPagina(1)
  }

  function alternarRecorte(alvo: Recorte) {
    setRecorte((atual) => (atual === alvo ? '' : alvo))
    setPagina(1)
  }

  async function copiarChave(chave: string) {
    try {
      await navigator.clipboard.writeText(chave)
      setAviso('Chave de acesso copiada.')
    } catch {
      setAviso('O navegador não deixou copiar. A chave está na ficha da nota.')
    }
  }

  async function lancar(linha: LinhaNota) {
    setAviso('')
    setErro('')
    setLancando(linha.chave_acesso)
    try {
      const resultado = await lancarNoEstoque(linha.chave_acesso)
      setVersao((n) => n + 1)
      setAviso(
        resultado.itens_lancados.length > 0
          ? `${resultado.itens_lancados.length} ${resultado.itens_lancados.length === 1 ? 'item entrou' : 'itens entraram'} no estoque pela nota ${linha.numero}.` +
            (resultado.itens_pendentes.length > 0
              ? ` ${resultado.itens_pendentes.length} continua(m) pendente(s) de conciliação.`
              : '')
          : 'Nenhum item foi lançado — todos já estavam lançados ou seguem pendentes.',
      )
    } catch (causa: unknown) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível lançar a nota no estoque.')
    } finally {
      setLancando(null)
    }
  }

  const rotuloDoPeriodo = mes ? (meses.find((m) => m.valor === mes)?.rotulo ?? mes) : 'todo o período'

  return (
    <LayoutApp
      titulo="Notas fiscais de compra"
      subtitulo={
        contagens
          ? `${contagens.total} ${contagens.total === 1 ? 'nota' : 'notas'} em ${rotuloDoPeriodo}`
          : 'Compras / Notas fiscais'
      }
      acoes={
        <LinkBotao para="/app/compras/notas-fiscais/importar" icone="documento_importar">
            Importar nota
          </LinkBotao>
      }
    >
      <div className="flex w-full flex-col gap-4">
        {erro && (
          <div role="alert" className="flex items-center gap-3 rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
            <Icone nome="error" tamanho={18} className="shrink-0" />
            <span className="min-w-0 flex-1">{erro}</span>
            <button
              type="button"
              onClick={() => setVersao((n) => n + 1)}
              className="h-8 shrink-0 rounded-sistema border border-erro bg-superficie px-3 text-[12.5px] font-semibold text-erro"
            >
              Tentar de novo
            </button>
          </div>
        )}

        {aviso && (
          <div role="status" className="flex items-center gap-3 rounded-sistema bg-ok-suave px-4 py-3 text-sm text-ok-texto">
            <Icone nome="check_circle" tamanho={18} className="shrink-0" />
            <span className="min-w-0 flex-1">{aviso}</span>
            <button
              type="button"
              onClick={() => setAviso('')}
              className="shrink-0 text-[12.5px] font-semibold underline-offset-2 hover:underline"
            >
              Fechar
            </button>
          </div>
        )}

        {/* Três recortes, todos clicáveis, todos um subconjunto de linhas. O
            valor total do período não vira KPI: ele já está no subtítulo e no
            pé da tabela, e não filtra nada. */}
        <div className="grid gap-4 sm:grid-cols-3">
          <CartaoKPI
            icone="error"
            tom={contagens && contagens.itens_pendentes > 0 ? 'erro' : 'neutro'}
            rotulo="Itens sem SKU"
            valor={String(contagens?.itens_pendentes ?? 0)}
            apoio={
              contagens && contagens.itens_pendentes > 0
                ? `em ${contagens.notas_com_pendencia} ${contagens.notas_com_pendencia === 1 ? 'nota' : 'notas'} · não entram no estoque`
                : 'toda a nota está conciliada'
            }
            ativo={recorte === 'pendentes'}
            aoClicar={() => alternarRecorte('pendentes')}
          />
          <CartaoKPI
            icone="inventory_2"
            tom={contagens && contagens.notas_a_lancar > 0 ? 'atencao' : 'neutro'}
            rotulo="Esperando lançar"
            valor={formatarMoeda(contagens?.valor_a_lancar_centavos ?? 0)}
            apoio={
              contagens && contagens.notas_a_lancar > 0
                ? `${contagens.notas_a_lancar} ${contagens.notas_a_lancar === 1 ? 'nota conciliada' : 'notas conciliadas'} · o saldo ainda não subiu`
                : 'nada conciliado esperando'
            }
            ativo={recorte === 'a_lancar'}
            aoClicar={() => alternarRecorte('a_lancar')}
          />
          <CartaoKPI
            icone="barcode_scanner"
            rotulo="Sem EAN no XML"
            valor={String(contagens?.itens_sem_ean ?? 0)}
            apoio={
              contagens && contagens.itens_sem_ean > 0
                ? `${contagens.notas_sem_ean} ${contagens.notas_sem_ean === 1 ? 'nota' : 'notas'} · exigem conciliar à mão`
                : 'todo item veio com código de barras'
            }
            ativo={recorte === 'sem_ean'}
            aoClicar={() => alternarRecorte('sem_ean')}
          />
        </div>

        {/* A barra fica SEMPRE, mesmo com a lista vazia: foi um filtro que
            produziu o vazio, e esconder o controle que causou o vazio é beco
            sem saída, não economia de tela. */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Chips
            rotuloDoGrupo="Situação no estoque"
            valor={situacao}
            aoTrocar={(chave) => {
              setSituacao(chave as SituacaoEstoque | '')
              setPagina(1)
            }}
            chips={[
              { chave: '', rotulo: 'Todas', contagem: contagens?.total, sempre: true },
              { chave: 'a_lancar', rotulo: 'A lançar', contagem: contagens?.a_lancar },
              { chave: 'parcial', rotulo: 'Parcial', contagem: contagens?.parcial },
              { chave: 'lancada', rotulo: 'Lançada', contagem: contagens?.lancada },
              { chave: 'cancelada', rotulo: 'Cancelada', contagem: contagens?.cancelada },
            ]}
          />

          <div className="flex flex-wrap items-center gap-2.5">
            <label className="sr-only" htmlFor="periodo">
              Período
            </label>
            <select
              id="periodo"
              value={mes}
              onChange={(evento) => {
                setMes(evento.target.value)
                setPagina(1)
              }}
              className="campo h-[34px] w-auto max-w-[190px] px-3 text-[13px] font-semibold capitalize"
            >
              <option value="">Todo o período</option>
              {meses.map((item) => (
                <option key={item.valor} value={item.valor} className="capitalize">
                  {item.rotulo}
                </option>
              ))}
            </select>

            <label className="sr-only" htmlFor="fornecedor">
              Fornecedor
            </label>
            <select
              id="fornecedor"
              value={fornecedor}
              onChange={(evento) => {
                setFornecedor(evento.target.value)
                setPagina(1)
              }}
              className="campo h-[34px] w-auto max-w-[220px] px-3 text-[13px] font-semibold"
            >
              <option value="">Todos os fornecedores</option>
              {fornecedores.map((f) => (
                <option key={f.codigo} value={f.codigo}>
                  {f.nome_fantasia || f.nome}
                </option>
              ))}
            </select>

            <label className="composto h-[34px] w-[280px] min-w-0 cursor-text">
              <Icone nome="search" tamanho={17} className="text-texto-fraco" />
              <input
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                placeholder="Buscar número, fornecedor ou chave"
              />
              <span className="sr-only">Buscar notas fiscais</span>
            </label>
          </div>
        </div>

        {!lista ? (
          <div className="h-[320px] animate-pulse rounded-sistema bg-superficie" />
        ) : (
          <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
            {lista.linhas.length === 0 ? (
              recortado ? (
                <VazioFiltrado
                  titulo={buscaAtiva ? `Nada para “${buscaAtiva}”` : 'Nenhuma nota neste recorte'}
                  descricao={`Existem ${lista.contagens.total} notas em ${rotuloDoPeriodo}. Nenhuma delas combina com esse recorte.`}
                  aoLimpar={limparRecorte}
                />
              ) : (
                <VazioPrimeiraVez
                  icone="receipt_long"
                  titulo={mes ? `Nenhuma nota em ${rotuloDoPeriodo}` : 'Nenhuma nota fiscal importada'}
                  descricao="A nota de entrada registra o que chegou do fornecedor. O caminho curto é importar o XML da NF-e — o arquivo preenche, você confere antes de gravar."
                  acao={
                    <LinkBotao para="/app/compras/notas-fiscais/importar" icone="documento_importar">
                        Importar a primeira
                      </LinkBotao>
                  }
                />
              )
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[940px] border-collapse">
                    <caption className="sr-only">Notas fiscais de compra importadas</caption>
                    <thead>
                      <tr className="bg-fundo">
                        {[
                          { rotulo: 'NF-e', alinhamento: 'text-left' },
                          { rotulo: 'Fornecedor', alinhamento: 'text-left' },
                          { rotulo: 'Emissão', alinhamento: 'text-left' },
                          { rotulo: 'Itens', alinhamento: 'text-right' },
                          { rotulo: 'Valor total', alinhamento: 'text-right' },
                          { rotulo: 'Estoque', alinhamento: 'text-left' },
                          { rotulo: '', alinhamento: 'text-right' },
                        ].map((coluna) => (
                          <th
                            key={coluna.rotulo || 'acoes'}
                            scope="col"
                            className={`border-b border-borda px-3 py-2.5 text-[12.5px] font-semibold text-texto-suave first:pl-4 last:pr-4 ${coluna.alinhamento}`}
                          >
                            {coluna.rotulo}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lista.linhas.map((linha, i) => (
                        <LinhaDaTabela
                          key={linha.chave_acesso}
                          linha={linha}
                          ultima={i === lista.linhas.length - 1}
                          aberta={chaveAberta === linha.chave_acesso}
                          lancando={lancando === linha.chave_acesso}
                          aoAbrir={() => setChaveAberta(linha.chave_acesso)}
                          aoCopiarChave={() => void copiarChave(linha.chave_acesso)}
                          aoLancar={() => void lancar(linha)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                <RodapeDeSomas lista={lista} />

                <Paginacao
                  pagina={lista.pagina}
                  paginas={lista.paginas}
                  total={lista.total}
                  tamanho={lista.tamanho}
                  substantivo="nota"
                  aoTrocarPagina={setPagina}
                />
              </>
            )}
          </div>
        )}
      </div>

      {chaveAberta && (
        <GavetaDaNota
          chave={chaveAberta}
          aoFechar={() => {
            setChaveAberta(null)
            setVersao((n) => n + 1) // a lista pode ter mudado (conciliação/lançamento)
          }}
        />
      )}
    </LayoutApp>
  )
}

/* ------------------------------------------------------------------ linha */

function LinhaDaTabela({
  linha,
  ultima,
  aberta,
  lancando,
  aoAbrir,
  aoCopiarChave,
  aoLancar,
}: {
  linha: LinhaNota
  ultima: boolean
  aberta: boolean
  lancando: boolean
  aoAbrir: () => void
  aoCopiarChave: () => void
  aoLancar: () => void
}) {
  const borda = ultima ? '' : 'border-b border-divisoria'
  const cancelada = linha.situacao_estoque === 'cancelada'
  const naoLancados = Math.max(linha.itens - linha.itens_lancados, 0)

  const acoes: AcaoDeLinha[] = [
    { rotulo: 'Ver a nota', icone: 'visibility', aoEscolher: aoAbrir },
    { rotulo: 'Copiar chave de acesso', icone: 'edit_note', aoEscolher: aoCopiarChave },
    {
      rotulo: lancando ? 'Lançando…' : 'Lançar no estoque',
      icone: 'inventory_2',
      aoEscolher: aoLancar,
      separar: true,
      razaoImpedida: cancelada
        ? 'nota cancelada'
        : naoLancados === 0
          ? 'todos os itens já foram lançados'
          : linha.itens_pendentes >= naoLancados
            ? 'nenhum item conciliado — abra a nota e concilie'
            : undefined,
    },
  ]

  return (
    <tr className={aberta ? 'bg-marca-fundo' : 'hover:bg-superficie-suave'}>
      <td className={`px-3 py-2.5 pl-4 ${borda}`}>
        <button
          type="button"
          onClick={aoAbrir}
          className="text-left text-[13.5px] font-semibold text-tinta hover:text-marca"
        >
          <span className="tabular">{linha.numero}</span>
        </button>
        <div className="mt-0.5 text-xs text-texto-suave">
          série <span className="tabular">{linha.serie || '—'}</span> · mod 55
        </div>
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <div className="text-[13.5px] font-semibold text-tinta">{linha.fornecedor.nome}</div>
        <div className="tabular mt-0.5 text-xs text-texto-suave">{linha.fornecedor.codigo}</div>
      </td>

      <td className={`tabular px-3 py-2.5 text-[13px] text-texto ${borda}`}>
        {new Date(linha.emitida_em).toLocaleDateString('pt-BR')}
      </td>

      <td className={`px-3 py-2.5 text-right ${borda}`}>
        <span className="tabular text-[13px] text-tinta">{linha.itens}</span>
        {linha.itens_pendentes > 0 && (
          <div className="tabular mt-0.5 text-[11.5px] font-semibold text-erro">
            {linha.itens_pendentes} sem SKU
          </div>
        )}
      </td>

      {/* Nota cancelada tem o valor apagado: está fora das somas, e a linha
          inteira precisa dizer isso. */}
      <td
        className={`tabular px-3 py-2.5 text-right text-[13px] ${borda} ${
          cancelada ? 'text-texto-suave line-through' : 'font-semibold text-tinta'
        }`}
      >
        {formatarMoeda(linha.valor_total_centavos)}
      </td>

      <td className={`px-3 py-2.5 ${borda}`}>
        <SeloDeEstoque linha={linha} />
      </td>

      <td className={`px-3 py-2.5 pr-4 ${borda}`}>
        <MenuDeLinha rotulo={`Ações da nota ${linha.numero}`} acoes={acoes} />
      </td>
    </tr>
  )
}

/**
 * O selo da coluna Estoque, com o que falta escrito embaixo.
 *
 * Cor só no que pede ação: "Lançada" é o desfecho normal e fica quieta em
 * cinza. Verde em quase toda linha não classificaria nada (spec 01).
 */
function SeloDeEstoque({ linha }: { linha: LinhaNota }) {
  if (linha.situacao_estoque === 'cancelada') {
    return (
      <>
        <Selo tom="riscado">Cancelada</Selo>
        <span className="mt-0.5 block text-[11.5px] text-texto-suave">fora das somas</span>
      </>
    )
  }

  if (linha.situacao_estoque === 'lancada') {
    return (
      <>
        <Selo tom="neutro">
          Lançada
        </Selo>
        <span className="mt-0.5 block text-[11.5px] text-texto-suave">
          {linha.itens} {linha.itens === 1 ? 'item entrou' : 'itens entraram'} no estoque
        </span>
      </>
    )
  }

  if (linha.situacao_estoque === 'parcial') {
    return (
      <>
        <Selo tom="atencao">
          Parcial
        </Selo>
        <span className="mt-0.5 block text-[11.5px] text-texto-suave">
          <span className="tabular">
            {linha.itens_lancados}/{linha.itens}
          </span>{' '}
          lançados · falta {formatar(linha.valor_a_lancar_centavos)}
        </span>
      </>
    )
  }

  return (
    <>
      <Selo tom="atencao">
        A lançar
      </Selo>
      <span className="mt-0.5 block text-[11.5px] text-texto-suave">
        {linha.itens_pendentes > 0
          ? `${linha.itens_pendentes} ${linha.itens_pendentes === 1 ? 'item precisa' : 'itens precisam'} de SKU`
          : 'conciliada, pronta para lançar'}
      </span>
    </>
  )
}

/* ----------------------------------------------------------------- somas */

/**
 * O pé soma o RECORTE, não a página — é o que o filtro prometeu.
 *
 * As três parcelas explicam o total: o que já virou saldo, o que está pronto
 * para virar e o que nem conciliado está. Um total sozinho não diria em que pé
 * está o trabalho.
 */
function RodapeDeSomas({ lista }: { lista: ListaDeNotas }) {
  const { somas, total, contagens } = lista

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border-t border-borda bg-fundo px-4 py-3">
      <span className="text-[13px] text-texto-suave">
        Soma do recorte · <span className="tabular">{total}</span> {total === 1 ? 'nota' : 'notas'}
        {contagens.cancelada > 0 && ', canceladas fora'}
      </span>
      <div className="flex flex-wrap gap-x-7 gap-y-2">
        {[
          { rotulo: 'Total', valor: somas.total_centavos, cor: 'text-tinta' },
          { rotulo: 'Já no estoque', valor: somas.lancado_centavos, cor: 'text-tinta' },
          { rotulo: 'A lançar', valor: somas.a_lancar_centavos, cor: 'text-atencao-texto' },
          { rotulo: 'Sem SKU', valor: somas.pendente_centavos, cor: 'text-texto-suave' },
        ].map((soma) => (
          <div key={soma.rotulo}>
            <div className="text-[11px] font-bold tracking-[0.6px] text-texto-suave uppercase">
              {soma.rotulo}
            </div>
            <div className={`tabular text-[14px] font-semibold ${soma.cor}`}>{formatar(soma.valor)}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- gaveta */

/**
 * A ficha da nota em gaveta.
 *
 * `larga` (760px) e não os 480px da spec 01: a ficha traz a tabela de itens
 * inteira, com descrição, SKU conciliado, quantidade, unitário e total. Em 520
 * a coluna de descrição some. O desvio está registrado em docs/de-para.md.
 */
function GavetaDaNota({ chave, aoFechar }: { chave: string; aoFechar: () => void }) {
  return (
    <Gaveta rotulo={`Nota fiscal ${chave}`} largura="larga" aoFechar={aoFechar}>
      <CabecalhoDaGaveta
        titulo="Nota fiscal"
        subtitulo={chave}
        rotuloFechar="Fechar nota"
        aoFechar={aoFechar}
      />
      <CorpoDaGaveta>
        <FichaNotaFiscal chave={chave} />
      </CorpoDaGaveta>
    </Gaveta>
  )
}
